// 🔄 배치 마이그레이션 스크립트 - 기존 오염 ID 데이터 정리
// [배경] device_catalog와 unified_prices의 오염된 ID를 canonical 형식으로 정리
// [실행] 브라우저 콘솔에서 window.__migrateToCanonicalIds() 호출

import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  writeBatch,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  generateModelId,
  generatePriceId,
  extractPureModelId,
  isCanonicalModelId,
  generateAliases,
  DeviceCatalogDocument,
  UnifiedPriceDocument
} from '../types/deviceCatalog';
import { IDAliasService } from '../services/idAliasService';

// 🔑 마이그레이션 결과 인터페이스
interface MigrationResult {
  success: boolean;
  catalog: {
    total: number;
    nonCanonical: number;
    migrated: number;
    errors: string[];
  };
  prices: {
    total: number;
    nonCanonical: number;
    migrated: number;
    errors: string[];
  };
  aliases: {
    registered: number;
  };
}

/**
 * 🔍 Step 1: device_catalog에서 비정상 ID 문서 스캔
 */
async function scanNonCanonicalCatalogDocs(): Promise<{
  normal: DeviceCatalogDocument[];
  corrupted: Array<{ doc: DeviceCatalogDocument; suggestedId: string; issues: string[] }>;
}> {
  console.log('🔍 Step 1: device_catalog 비정상 ID 스캔...');
  
  const snapshot = await getDocs(collection(db, 'device_catalog'));
  
  const normal: DeviceCatalogDocument[] = [];
  const corrupted: Array<{ doc: DeviceCatalogDocument; suggestedId: string; issues: string[] }> = [];
  
  snapshot.docs.forEach(docSnap => {
    const data = { id: docSnap.id, ...docSnap.data() } as DeviceCatalogDocument;
    
    if (isCanonicalModelId(data.id)) {
      normal.push(data);
    } else {
      const issues: string[] = [];
      
      // 문제 유형 식별
      if (/-\d+(gb|tb)-/i.test(data.id)) issues.push('storage 포함');
      if (/-\d+$/.test(data.id) && !/\d+(gb|tb|inch|mm)$/i.test(data.id)) issues.push('순번 포함');
      if (/-wifi-|-cellular-/i.test(data.id)) issues.push('connectivity 포함');
      
      const suggestedId = extractPureModelId(data.id);
      
      corrupted.push({ doc: data, suggestedId, issues });
    }
  });
  
  console.log(`📊 스캔 결과: 정상 ${normal.length}개, 비정상 ${corrupted.length}개`);
  
  return { normal, corrupted };
}

/**
 * 🔍 Step 2: unified_prices에서 비정상 ID 문서 스캔
 */
async function scanNonCanonicalPriceDocs(): Promise<{
  normal: UnifiedPriceDocument[];
  corrupted: Array<{ doc: UnifiedPriceDocument; suggestedModelId: string; issues: string[] }>;
}> {
  console.log('🔍 Step 2: unified_prices 비정상 ID 스캔...');
  
  const snapshot = await getDocs(collection(db, 'unified_prices'));
  
  const normal: UnifiedPriceDocument[] = [];
  const corrupted: Array<{ doc: UnifiedPriceDocument; suggestedModelId: string; issues: string[] }> = [];
  
  snapshot.docs.forEach(docSnap => {
    const data = { id: docSnap.id, ...docSnap.data() } as UnifiedPriceDocument;
    
    // modelId 필드 검사
    const modelId = data.modelId || '';
    
    if (isCanonicalModelId(modelId)) {
      normal.push(data);
    } else {
      const issues: string[] = [];
      
      if (/-\d+(gb|tb)-/i.test(modelId)) issues.push('modelId에 storage 포함');
      if (/-\d+$/.test(modelId) && !/\d+(gb|tb|inch|mm)$/i.test(modelId)) issues.push('modelId에 순번 포함');
      if (/-wifi-|-cellular-/i.test(modelId)) issues.push('modelId에 connectivity 포함');
      
      const suggestedModelId = extractPureModelId(modelId);
      
      corrupted.push({ doc: data, suggestedModelId, issues });
    }
  });
  
  console.log(`📊 스캔 결과: 정상 ${normal.length}개, 비정상 ${corrupted.length}개`);
  
  return { normal, corrupted };
}

/**
 * 🔄 Step 3: device_catalog 마이그레이션
 * - 비정상 ID → canonical ID로 새 문서 생성
 * - 기존 문서와 병합 (storageOptions 등)
 * - alias 등록
 */
async function migrateCatalogDocs(
  corrupted: Array<{ doc: DeviceCatalogDocument; suggestedId: string; issues: string[] }>,
  existingCanonical: Map<string, DeviceCatalogDocument>
): Promise<{ migrated: number; errors: string[] }> {
  console.log('🔄 Step 3: device_catalog 마이그레이션...');
  
  const result = { migrated: 0, errors: [] as string[] };
  
  // 배치 처리
  const BATCH_SIZE = 400;
  
  for (let i = 0; i < corrupted.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    const chunk = corrupted.slice(i, i + BATCH_SIZE);
    
    for (const { doc: corruptedDoc, suggestedId } of chunk) {
      try {
        const existing = existingCanonical.get(suggestedId);
        
        if (existing) {
          // 기존 문서에 옵션 병합
          const mergedStorageOptions = Array.from(new Set([
            ...existing.storageOptions,
            ...corruptedDoc.storageOptions
          ]));
          
          const mergedConnectivityOptions = Array.from(new Set([
            ...(existing.connectivityOptions || []),
            ...(corruptedDoc.connectivityOptions || [])
          ]));
          
          batch.update(doc(db, 'device_catalog', suggestedId), {
            storageOptions: mergedStorageOptions,
            connectivityOptions: mergedConnectivityOptions,
            updatedAt: Timestamp.now(),
            updatedBy: 'migration-script'
          });
        } else {
          // 새 canonical 문서 생성
          const newDoc: DeviceCatalogDocument = {
            ...corruptedDoc,
            id: suggestedId,
            updatedAt: new Date(),
            updatedBy: 'migration-script'
          };
          
          batch.set(doc(db, 'device_catalog', suggestedId), newDoc);
          existingCanonical.set(suggestedId, newDoc);
        }
        
        // 기존 오염 문서는 삭제 (canonical과 다른 경우에만)
        if (corruptedDoc.id !== suggestedId) {
          batch.delete(doc(db, 'device_catalog', corruptedDoc.id));
        }
        
        // alias 등록
        await IDAliasService.registerAlias(corruptedDoc.id, suggestedId, 'model');
        
        result.migrated++;
      } catch (error) {
        result.errors.push(`${corruptedDoc.id}: ${(error as Error).message}`);
      }
    }
    
    try {
      await batch.commit();
      console.log(`✅ 배치 ${Math.ceil((i + BATCH_SIZE) / BATCH_SIZE)} 완료: ${Math.min(i + BATCH_SIZE, corrupted.length)}/${corrupted.length}`);
    } catch (error) {
      result.errors.push(`배치 커밋 실패: ${(error as Error).message}`);
    }
  }
  
  return result;
}

/**
 * 🔄 Step 4: unified_prices 마이그레이션
 * - modelId를 canonical로 업데이트
 * - priceId도 재생성
 */
async function migratePriceDocs(
  corrupted: Array<{ doc: UnifiedPriceDocument; suggestedModelId: string; issues: string[] }>
): Promise<{ migrated: number; errors: string[] }> {
  console.log('🔄 Step 4: unified_prices 마이그레이션...');
  
  const result = { migrated: 0, errors: [] as string[] };
  
  const BATCH_SIZE = 400;
  
  for (let i = 0; i < corrupted.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    const chunk = corrupted.slice(i, i + BATCH_SIZE);
    
    for (const { doc: corruptedDoc, suggestedModelId } of chunk) {
      try {
        // 새 canonical priceId 생성
        const newPriceId = IDAliasService.createCanonicalPriceId(
          suggestedModelId,
          {
            storage: corruptedDoc.variant?.storage || '기본',
            connectivity: corruptedDoc.variant?.connectivity
          }
        );
        
        // 새 문서 생성
        const newDoc: UnifiedPriceDocument = {
          ...corruptedDoc,
          id: newPriceId,
          modelId: suggestedModelId,
          updatedAt: new Date(),
          updatedBy: 'migration-script'
        };
        
        batch.set(doc(db, 'unified_prices', newPriceId), newDoc);
        
        // 기존 오염 문서 삭제 (priceId가 다른 경우)
        if (corruptedDoc.id !== newPriceId) {
          batch.delete(doc(db, 'unified_prices', corruptedDoc.id));
          
          // alias 등록
          await IDAliasService.registerAlias(corruptedDoc.id, newPriceId, 'price');
        }
        
        result.migrated++;
      } catch (error) {
        result.errors.push(`${corruptedDoc.id}: ${(error as Error).message}`);
      }
    }
    
    try {
      await batch.commit();
      console.log(`✅ 배치 ${Math.ceil((i + BATCH_SIZE) / BATCH_SIZE)} 완료: ${Math.min(i + BATCH_SIZE, corrupted.length)}/${corrupted.length}`);
    } catch (error) {
      result.errors.push(`배치 커밋 실패: ${(error as Error).message}`);
    }
  }
  
  return result;
}

/**
 * 🔥 전체 마이그레이션 실행
 */
export async function migrateToCanonicalIds(): Promise<MigrationResult> {
  console.log('🔥 ID Canonical 마이그레이션 시작...');
  console.log('============================================');
  
  const result: MigrationResult = {
    success: false,
    catalog: { total: 0, nonCanonical: 0, migrated: 0, errors: [] },
    prices: { total: 0, nonCanonical: 0, migrated: 0, errors: [] },
    aliases: { registered: 0 }
  };
  
  try {
    // Step 1: device_catalog 스캔
    const catalogScan = await scanNonCanonicalCatalogDocs();
    result.catalog.total = catalogScan.normal.length + catalogScan.corrupted.length;
    result.catalog.nonCanonical = catalogScan.corrupted.length;
    
    // Step 2: unified_prices 스캔
    const priceScan = await scanNonCanonicalPriceDocs();
    result.prices.total = priceScan.normal.length + priceScan.corrupted.length;
    result.prices.nonCanonical = priceScan.corrupted.length;
    
    // 문제 문서 요약 출력
    console.log('\n📋 비정상 문서 요약:');
    console.log(`  device_catalog: ${catalogScan.corrupted.length}개`);
    catalogScan.corrupted.slice(0, 5).forEach(({ doc, suggestedId, issues }) => {
      console.log(`    - ${doc.id} → ${suggestedId} (${issues.join(', ')})`);
    });
    if (catalogScan.corrupted.length > 5) {
      console.log(`    ... 외 ${catalogScan.corrupted.length - 5}개`);
    }
    
    console.log(`\n  unified_prices: ${priceScan.corrupted.length}개`);
    priceScan.corrupted.slice(0, 5).forEach(({ doc, suggestedModelId, issues }) => {
      console.log(`    - ${doc.modelId} → ${suggestedModelId} (${issues.join(', ')})`);
    });
    if (priceScan.corrupted.length > 5) {
      console.log(`    ... 외 ${priceScan.corrupted.length - 5}개`);
    }
    
    // 마이그레이션 실행 확인
    if (catalogScan.corrupted.length === 0 && priceScan.corrupted.length === 0) {
      console.log('\n✅ 모든 ID가 이미 canonical 형식입니다. 마이그레이션 불필요.');
      result.success = true;
      return result;
    }
    
    // Step 3: device_catalog 마이그레이션
    if (catalogScan.corrupted.length > 0) {
      const existingCanonical = new Map<string, DeviceCatalogDocument>();
      catalogScan.normal.forEach(doc => existingCanonical.set(doc.id, doc));
      
      const catalogResult = await migrateCatalogDocs(catalogScan.corrupted, existingCanonical);
      result.catalog.migrated = catalogResult.migrated;
      result.catalog.errors = catalogResult.errors;
    }
    
    // Step 4: unified_prices 마이그레이션
    if (priceScan.corrupted.length > 0) {
      const priceResult = await migratePriceDocs(priceScan.corrupted);
      result.prices.migrated = priceResult.migrated;
      result.prices.errors = priceResult.errors;
    }
    
    result.success = result.catalog.errors.length === 0 && result.prices.errors.length === 0;
    
    // 결과 출력
    console.log('\n============================================');
    console.log('📊 마이그레이션 결과:');
    console.log(`  device_catalog: ${result.catalog.migrated}/${result.catalog.nonCanonical}개 마이그레이션`);
    console.log(`  unified_prices: ${result.prices.migrated}/${result.prices.nonCanonical}개 마이그레이션`);
    
    if (result.catalog.errors.length > 0) {
      console.log(`\n❌ device_catalog 에러: ${result.catalog.errors.length}개`);
      result.catalog.errors.slice(0, 5).forEach(e => console.log(`    - ${e}`));
    }
    
    if (result.prices.errors.length > 0) {
      console.log(`\n❌ unified_prices 에러: ${result.prices.errors.length}개`);
      result.prices.errors.slice(0, 5).forEach(e => console.log(`    - ${e}`));
    }
    
    if (result.success) {
      console.log('\n🎉 마이그레이션 완료!');
    } else {
      console.log('\n⚠️ 일부 에러가 발생했습니다. 로그를 확인하세요.');
    }
    
    return result;
    
  } catch (error) {
    console.error('❌ 마이그레이션 실패:', error);
    throw error;
  }
}

/**
 * 🔍 마이그레이션 미리보기 (실제 변경 없음)
 */
export async function previewMigration(): Promise<void> {
  console.log('🔍 마이그레이션 미리보기 (Dry Run)...');
  console.log('============================================');
  
  // device_catalog 스캔
  const catalogScan = await scanNonCanonicalCatalogDocs();
  
  console.log('\n📋 device_catalog 비정상 ID:');
  catalogScan.corrupted.forEach(({ doc, suggestedId, issues }) => {
    console.log(`  ${doc.id}`);
    console.log(`    → ${suggestedId}`);
    console.log(`    문제: ${issues.join(', ')}`);
    console.log(`    모델: ${doc.modelKo}`);
    console.log('');
  });
  
  // unified_prices 스캔
  const priceScan = await scanNonCanonicalPriceDocs();
  
  console.log('\n📋 unified_prices 비정상 modelId:');
  priceScan.corrupted.forEach(({ doc, suggestedModelId, issues }) => {
    console.log(`  ${doc.id}`);
    console.log(`    modelId: ${doc.modelId} → ${suggestedModelId}`);
    console.log(`    문제: ${issues.join(', ')}`);
    console.log('');
  });
  
  console.log('============================================');
  console.log(`📊 요약: device_catalog ${catalogScan.corrupted.length}개, unified_prices ${priceScan.corrupted.length}개`);
}

// 🎯 브라우저 콘솔에서 실행할 수 있도록 window에 등록
if (typeof window !== 'undefined') {
  (window as any).__migrateToCanonicalIds = migrateToCanonicalIds;
  (window as any).__previewMigration = previewMigration;
  console.log('🔧 마이그레이션 함수 등록 완료:');
  console.log('  - window.__previewMigration(): 마이그레이션 미리보기 (변경 없음)');
  console.log('  - window.__migrateToCanonicalIds(): 실제 마이그레이션 실행');
}







